/**
 * TravelExpenseQuickAdd — tiny dialog for adding an expense without
 * leaving the trip editor. Keeps enough fields for the fast path:
 * amount, currency, category, description, date, paid-by, optional
 * split participants.
 *
 * The caller owns the "submit" contract by passing `onSubmit` — that's
 * the same `createExpense` the useTravelBudget hook exposes, letting
 * this component be reused from the budget panel, day editor, and event
 * cards without each needing its own hook wiring.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TravelExpenseCategory } from '../api';
import type { CreateExpenseVars } from '../hooks';

/** Common currencies surfaced first. Users can type any 3-letter code. */
const CURRENCY_OPTIONS = [
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'CHF',
  'CAD',
  'AUD',
  'NZD',
  'SGD',
  'HKD',
  'CNY',
  'INR',
  'KRW',
  'MXN',
  'BRL',
  'ZAR',
  'THB',
  'VND',
];

const CATEGORY_OPTIONS: TravelExpenseCategory[] = [
  'lodging',
  'food',
  'transport',
  'activity',
  'other',
];

interface Props {
  trigger: React.ReactNode;
  defaultItemId: string | null;
  defaultEventId: string | null;
  defaultCurrency: string;
  defaultIncurredAt?: string;
  onSubmit: (input: CreateExpenseVars) => Promise<unknown>;
  isSubmitting: boolean;
}

export function TravelExpenseQuickAdd({
  trigger,
  defaultItemId,
  defaultEventId,
  defaultCurrency,
  defaultIncurredAt,
  onSubmit,
  isSubmitting,
}: Props) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency.toUpperCase());
  const [category, setCategory] = useState<TravelExpenseCategory>('food');
  const [description, setDescription] = useState('');
  const [incurredAt, setIncurredAt] = useState(
    defaultIncurredAt ?? new Date().toISOString().slice(0, 10),
  );
  const [paidBy, setPaidBy] = useState('');
  const [participantNames, setParticipantNames] = useState('');
  // Per-participant percentage overrides. A name missing here defaults to the
  // even-split percentage — so unless the user customizes, splits stay equal.
  const [percents, setPercents] = useState<Record<string, string>>({});

  const parsedNames = useMemo(
    () =>
      participantNames
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [participantNames],
  );
  const evenPercent = parsedNames.length > 0 ? 100 / parsedNames.length : 0;

  // Drop percent overrides for names the user removed.
  const parsedKey = parsedNames.join('|');
  useEffect(() => {
    setPercents((prev) => {
      const next: Record<string, string> = {};
      for (const name of parsedNames) {
        if (prev[name] !== undefined) next[name] = prev[name];
      }
      return next;
    });
    // parsedKey tracks the name set; parsedNames is not referenced beyond the snapshot captured above.
  }, [parsedKey]);

  const percentFor = (name: string): number => {
    const raw = percents[name];
    if (raw === undefined || raw === '') return evenPercent;
    const n = parseFloat(raw);
    return Number.isFinite(n) && n >= 0 ? n : evenPercent;
  };
  const percentSum = parsedNames.reduce((sum, n) => sum + percentFor(n), 0);
  const sumOff = parsedNames.length > 0 && Math.abs(percentSum - 100) > 0.01;

  // Reset when opening so the form starts clean, honoring new defaults.
  useEffect(() => {
    if (open) {
      setAmount('');
      setCurrency(defaultCurrency.toUpperCase());
      setCategory('food');
      setDescription('');
      setIncurredAt(defaultIncurredAt ?? new Date().toISOString().slice(0, 10));
      setPaidBy('');
      setParticipantNames('');
      setPercents({});
    }
  }, [open, defaultCurrency, defaultIncurredAt]);

  const submit = async () => {
    const amountNum = parseFloat(amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) return;
    const amountCents = Math.round(amountNum * 100);
    const cur = currency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(cur)) return;

    // share is a numeric weight — the server divides proportionally, so passing
    // the percentage directly preserves the user's intended ratio.
    const participants = parsedNames.map((name) => ({ name, share: percentFor(name) }));

    const split: CreateExpenseVars['split'] =
      participants.length > 0
        ? {
            participants,
            ...(paidBy.trim() ? { paidBy: paidBy.trim() } : {}),
          }
        : undefined;

    try {
      await onSubmit({
        itemId: defaultItemId,
        eventId: defaultEventId,
        category,
        description: description.trim() || null,
        amountCents,
        currency: cur,
        incurredAt,
        paidBy: paidBy.trim() || null,
        split,
      });
      setOpen(false);
    } catch {
      // leave dialog open so the user can retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add expense</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="expense-amount" className="text-xs">
                Amount
              </Label>
              <Input
                id="expense-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="50.00"
              />
            </div>
            <div className="w-28">
              <Label htmlFor="expense-currency" className="text-xs">
                Currency
              </Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="expense-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="expense-category" className="text-xs">
              Category
            </Label>
            <Select value={category} onValueChange={(v) => setCategory(v as TravelExpenseCategory)}>
              <SelectTrigger id="expense-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="expense-description" className="text-xs">
              Description
            </Label>
            <Textarea
              id="expense-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Dinner at Le Comptoir"
              rows={2}
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="expense-date" className="text-xs">
                Date
              </Label>
              <Input
                id="expense-date"
                type="date"
                value={incurredAt}
                onChange={(e) => setIncurredAt(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="expense-paid-by" className="text-xs">
                Paid by
              </Label>
              <Input
                id="expense-paid-by"
                value={paidBy}
                onChange={(e) => setPaidBy(e.target.value)}
                placeholder="e.g. Alex"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="expense-participants" className="text-xs">
              Split between (comma-separated names)
            </Label>
            <Input
              id="expense-participants"
              value={participantNames}
              onChange={(e) => setParticipantNames(e.target.value)}
              placeholder="Alex, Sam, Jordan"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Leave blank to record without splitting. Each participant starts on an equal share —
              adjust the percentages below to customize.
            </p>
          </div>

          {parsedNames.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-border/60 p-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Share per person</Label>
                <button
                  type="button"
                  className="text-[10px] text-muted-foreground underline hover:text-foreground"
                  onClick={() => setPercents({})}
                >
                  Distribute evenly
                </button>
              </div>
              <div className="space-y-1">
                {parsedNames.map((name) => (
                  <div key={name} className="flex items-center gap-2">
                    <span className="flex-1 truncate text-xs">{name}</span>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={percents[name] ?? evenPercent.toFixed(1)}
                      onChange={(e) => setPercents((prev) => ({ ...prev, [name]: e.target.value }))}
                      className="h-7 w-20 text-xs"
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                ))}
              </div>
              {sumOff && (
                <p className="text-[10px] text-amber-600">
                  Percentages add up to {percentSum.toFixed(1)}% — they should sum to 100%.
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isSubmitting || !amount || sumOff}>
            {isSubmitting ? 'Saving…' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
